import { Typography } from '@mui/material';
import { BaseDeactivationAlert } from 'features/geoEvents/components/BaseDeactivationAlert';
import { useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';

type FormValues = {
  acreage: number | null;
  containment: number | null;
  isPrescribed: boolean;
  isFps: boolean;
  manualDeactivationStarted: boolean | null;
};

type WildfireDeactivationAlertProps = {
  hasEvacZones: boolean;
};

type WildfireDeactivationTitleProps = {
  acreage: number;
  containment: number;
  manualDeactivationStarted: boolean;
};

const convertAcreageToHours = (acreage: number): number => {
  if (acreage > 25) return 8;
  if (acreage > 10) return 6;
  return 4;
};

const WildfireDeactivationTitle = (
  props: WildfireDeactivationTitleProps,
): JSX.Element => {
  const { acreage, containment, manualDeactivationStarted } = props;

  if (manualDeactivationStarted) {
    return (
      <Typography>
        <Trans i18nKey="editGeoEvent.deactivationStarted.manual">
          Incident deactivation timer started -
          <b>Approximately 6 hours from Manual Deactivation</b>.
        </Trans>
      </Typography>
    );
  }

  if (containment === 100) {
    return (
      <Typography>
        <Trans
          i18nKey="createEditWildfire.deactivationStarted.containment"
          values={{ hours: convertAcreageToHours(acreage) }}
        >
          Incident deactivation timer started -
          <b>Approximately 4 hours from 100% containment</b>.
        </Trans>
      </Typography>
    );
  }

  // Fallback for FPS
  return (
    <Typography>
      <Trans i18nKey="createEditWildfire.deactivationStarted.fps">
        Incident deactivation timer started -
        <b>Approximately 6 hours from FPS</b>.
      </Trans>
    </Typography>
  );
};

export const WildfireDeactivationAlert = (
  props: WildfireDeactivationAlertProps,
): JSX.Element => {
  const { hasEvacZones } = props;
  const { watch } = useFormContext<FormValues>();

  const [acreage, containment, isPrescribed, isFps, manualDeactivationStarted] =
    watch([
      'acreage',
      'containment',
      'isPrescribed',
      'isFps',
      'manualDeactivationStarted',
    ]);

  const canFpsStartDeactivation = isFps && !containment;
  const fullContainment = containment === 100;

  const wildfireTriggers = canFpsStartDeactivation || fullContainment;

  return (
    <BaseDeactivationAlert
      hasEvacZones={hasEvacZones}
      isExternallyBlocked={isPrescribed}
      isExternallyTriggered={wildfireTriggers}
    >
      <WildfireDeactivationTitle
        acreage={acreage || 0}
        containment={containment || 0}
        manualDeactivationStarted={Boolean(manualDeactivationStarted)}
      />
    </BaseDeactivationAlert>
  );
};
